import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { onRefreshToken } from '../store/slices/auth/authSlice';
import { useEffect, useCallback, useState, useRef } from 'react';
import useSessionModal from '../hooks/useSessionModal';
import { refreshToken as refreshTokenService } from './../auth/services/authService';

const useSessionTimer = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const { openModal, closeModal } = useSessionModal();
  const [timeLeft, setTimeLeft] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null); // Referencia para manejar el intervalo

  const calculateTimeLeft = () => {
    const currentTime = Math.floor(Date.now() / 1000);
    return Math.max(auth.exp - currentTime, 0);
  };

  const handleSessionExpiry = useCallback(() => {
    setSessionExpired(true);
    openModal();
  }, [openModal]);

  useEffect(() => {
    if (auth.exp) {
      const initialTimeLeft = calculateTimeLeft();
      setTimeLeft(initialTimeLeft);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        setTimeLeft(prevTimeLeft => {
          if (prevTimeLeft <= 1) {
            clearInterval(intervalRef.current as NodeJS.Timeout);
            setSessionExpired(true);
            openModal();
            return 0;
          }
          return prevTimeLeft - 1;
        });
      }, 1000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [auth.exp, openModal]);

  /**
   * Función para refrescar la sesión.
   */
  const refreshSession = async () => {
    const storedAuth = JSON.parse(sessionStorage.getItem('auth') || '{}');
    const { refreshToken } = storedAuth;

    if (refreshToken) {
      try {
        const response = await refreshTokenService(refreshToken);
        if (response && response.status === 200) {
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
          const claims = JSON.parse(window.atob(newAccessToken.split(".")[1]));

          dispatch(onRefreshToken({ accessToken: newAccessToken, exp: claims.exp }));

          const updatedAuthData = {
            ...storedAuth,
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
            exp: claims.exp,
          };
          sessionStorage.setItem("auth", JSON.stringify(updatedAuthData));

          setSessionExpired(false);
          closeModal();
        } else {
          handleSessionExpiry();
        }
      } catch (error) {
        console.error("Error al refrescar el token:", error);
        handleSessionExpiry();
      }
    }
  };

  return {
    timeLeft,
    openModal,
    refreshSession,
    closeModal,
    sessionExpired,
  };
};

export default useSessionTimer;
